import {
  Box,
  Flex,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Tooltip,
  Tr
} from '@chakra-ui/react';
import {
  Badge,
  Banner,
  Button,
  ButtonIcon,
  ButtonText,
  Checkbox,
  FilterChip,
  Modal,
  useToast
} from '@morressier/alembic';
import { useFieldArray, useFormContext } from 'react-hook-form';
import { IAuthorsValue } from './AuthorsUpdateModal';
import { useContext, useEffect, useRef, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faExclamationTriangle,
  faInfoCircle
} from '@fortawesome/pro-light-svg-icons';
import {
  IAuthorWithContribution,
  IAuthorWithEmail
} from '@publishing-engine/domain/models';
import { ContributionsBadge } from 'src/components/Submission/ContributionsBadge';
import { Layout } from 'src/components/Layout';
import {
  faArrowLeft,
  faBarsFilter,
  faGripDotsVertical,
  faPencil,
  faSearch,
  faTrashAlt,
  faUserPlus
} from '@fortawesome/pro-regular-svg-icons';
import { useKeycloak } from 'src/components/AuthHOC';
import { useDuplicateEmails } from 'src/hooks/useDuplicateEmails';
import React from 'react';
import RinggoldBadge from 'src/components/RinggoldBadge';
import EditAuthorInformation from 'src/pages/Organization/public/Journal/SubmissionUpload/components/EditAuthorInformation';
import { OrcidIDIcon } from 'src/components/User/RenderSectionDetails';
import { DeleteAuthorsModal } from './DeleteAuthorsModal';
import { FormFieldAuthors } from '@publishing-engine/domain/types/article';
import { IAuthorRolesMap } from 'src/utils/groupAuthorByRoles';
import SortableRow from 'src/components/FormField/SortableRow';
import {
  closestCenter,
  DndContext,
  DragEndEvent,
  PointerSensor,
  useSensor,
  useSensors
} from '@dnd-kit/core';
import {
  restrictToParentElement,
  restrictToVerticalAxis
} from '@dnd-kit/modifiers';
import {
  SortableContext,
  verticalListSortingStrategy
} from '@dnd-kit/sortable';
import { SubmissionDataContext } from 'src/contexts/SubmissionDataProvider';
import { useSubmissionContextData } from 'src/contexts/SubmissionContextProvider';

export const DUPLICATE_EMAIL_ERROR_TYPE = 'duplicate_email_error';

interface Props {
  onClose: () => void;
  value?: IAuthorWithContribution[];
  config: FormFieldAuthors | null;
  authorRoleMap: IAuthorRolesMap;
  isRevision?: boolean;
}

const DEFAULT_VALUE = {
  firstName: '',
  lastName: '',
  email: '',
  affiliation: '',
  city: ''
};

export const AuthorsForm: React.FC<Props> = ({
  onClose,
  value,
  config,
  authorRoleMap,
  isRevision
}) => {
  const [showIncomplete, setShowIncomplete] = useState(false);
  const [incompleteRows, setIncompleteRows] = useState<number[]>([]);
  const [isAuthorEditModalOpen, setIsAuthorEditModalOpen] = useState(false);
  const [isAddNewAuthorModalOpen, setIsAddNewAuthorModalOpen] = useState(false);
  const [selectedAuthorIndex, setSelectedAuthorIndex] = useState<number | null>(
    null
  );

  const { orcidField, affiliationField, contributionsField } =
    config?.params.validations || {};

  const {
    control,
    trigger,
    formState: { errors },
    getValues,
    setValue,
    watch
  } = useFormContext<IAuthorsValue>();
  const toast = useToast();
  const resetAuthorDataToInitialState = () => {
    setValue('metadata.authors', initialAuthorsData.current);
    setValue('showDiscardChangesModal', false);
  };

  const { fields, insert, remove, move } = useFieldArray({
    control,
    name: 'metadata.authors'
  });

  const { submissionContext: submissionContextFromContextData } =
    useSubmissionContextData();
  const submissionContextFromData = useContext(
    SubmissionDataContext
  )?.submissionContext;
  const submissionContext =
    submissionContextFromContextData ?? submissionContextFromData;
  const reviewAuthorInformation =
    submissionContext?.config.cms.authorSubmissionForm.reviewAuthorInformation;

  const noResultsFound =
    fields.length === 0 || (showIncomplete && incompleteRows.length === 0);

  const authors = getValues('metadata.authors');
  const [selectedAuthors, setSelectedAuthors] = useState<string[]>([]);
  const [deleteAuthors, setDeleteAuthors] = useState<boolean>(false);
  const initialAuthorsData = useRef<IAuthorWithEmail[]>(value || []);
  const watchedAuthors = watch('metadata.authors');
  const { duplicateCount } = useDuplicateEmails(watchedAuthors || []);
  const hasDuplicateEmails = duplicateCount > 0;
  const errorObject = errors.metadata?.authors;
  const hasValidationErrors = !!errorObject;
  const { duplicateEmails } = useDuplicateEmails(fields);
  const initialAuthor = initialAuthorsData.current;
  const isSelectable = (author: IAuthorWithContribution) =>
    ![authorRoleMap['Submitting'], authorRoleMap['Corresponding']].includes(
      author.email
    );

  const handleRowCheckboxChange = (index: string) => {
    setSelectedAuthors(prev =>
      prev.includes(index) ? prev.filter(i => i !== index) : [...prev, index]
    );
  };

  const handleSelectAll = (checked: boolean) => {
    if (checked) {
      const selectableIndexes = fields
        .map(a => (isSelectable(a) ? a.id : null))
        .filter(i => i !== null);
      setSelectedAuthors(selectableIndexes);
    } else {
      setSelectedAuthors([]);
    }
  };

  const handleUpdateIncompleteIndices = () => {
    if (showIncomplete) {
      // Find incomplete rows
      const updatedIncompleteIndices = fields.reduce((acc, field, index) => {
        const fieldError = errors.metadata?.authors?.[index];
        const authorData = authors?.[index];
        const hasErrors = Boolean(
          fieldError?.firstName ||
            fieldError?.lastName ||
            fieldError?.email ||
            fieldError?.affiliation ||
            !authorData?.orcid
        );
        if (hasErrors) {
          acc.push(index);
        }
        return acc;
      }, [] as number[]);
      setIncompleteRows(updatedIncompleteIndices);
    }
  };

  const handleDeleteSelected = () => {
    const indicesToRemove = selectedAuthors.map(id =>
      fields.findIndex(a => a.id === id)
    );

    remove(indicesToRemove);
    setSelectedAuthors([]);
    toast({
      variant: 'success',
      title: `${indicesToRemove.length} author${
        indicesToRemove.length > 1 ? 's' : ''
      } deleted successfully.`
    });
    setDeleteAuthors(false);
  };

  useEffect(() => {
    handleUpdateIncompleteIndices();
  }, [fields, errors, showIncomplete]);

  const allSelectableIndexes = fields
    .map(a => (isSelectable(a) ? a.id : null))
    .filter(i => i !== null);

  const allSelected =
    allSelectableIndexes.length > 0 &&
    allSelectableIndexes.every(i => selectedAuthors.includes(i));

  const handleShowIncompleteToggle = () => {
    if (showIncomplete) {
      setIncompleteRows([]);
    } else {
      // Find incomplete rows
      const incompleteIndices = fields.reduce((acc, field, index) => {
        const fieldError = errors.metadata?.authors?.[index];
        const authorData = authors?.[index];
        const hasErrors = Boolean(
          fieldError?.firstName ||
            fieldError?.lastName ||
            fieldError?.email ||
            fieldError?.affiliation ||
            !authorData?.orcid
        );
        if (hasErrors) {
          acc.push(index);
        }
        return acc;
      }, [] as number[]);
      setIncompleteRows(incompleteIndices);
    }
    setShowIncomplete(prev => !prev);
  };

  const renderEmptyState = () => {
    return (
      <Flex
        flexDirection="column"
        gap={8}
        textAlign="center"
        data-cy="empty-state"
      >
        <Box
          bgColor="neutralSecondaryFill"
          padding={8}
          data-cy="empty-state-icon"
        >
          <FontAwesomeIcon icon={faSearch} fontSize={40} />
        </Box>
        <Flex flexDirection="column" gap={2}>
          <Text textStyle="headingTitle" data-cy="empty-state-title">
            No Results Found
          </Text>
          <Text
            textStyle="textBody"
            paddingX={2}
            data-cy="empty-state-description"
          >
            Try adjusting your filters or clearing them to see more results.
          </Text>
        </Flex>
        <Flex justifyContent="center">
          <Button
            onClick={() => setShowIncomplete(false)}
            size="regular"
            data-cy="clear-filters-button"
          >
            Clear Filters
          </Button>
        </Flex>
      </Flex>
    );
  };

  const handleAuthorEditModal = (index: number) => {
    setSelectedAuthorIndex(index);
    setIsAuthorEditModalOpen(true);
  };

  const handleCloseEditModal = () => {
    setSelectedAuthorIndex(null);
    setIsAuthorEditModalOpen(false);
  };

  const onApply = (data: IAuthorWithContribution) => {
    if (selectedAuthorIndex !== null) {
      const authorData = watch(`metadata.authors.${selectedAuthorIndex}`);
      const {
        affiliation,
        country,
        countryCode,
        city,
        postalCode,
        ringgoldId
      } = authorData;
      setValue(
        `metadata.authors.${selectedAuthorIndex}`,
        {
          ...data,
          affiliation,
          country,
          countryCode,
          city,
          postalCode,
          ringgoldId
        },
        {
          shouldDirty: true,
          shouldValidate: true
        }
      );
      handleCloseEditModal();
      setIsAddNewAuthorModalOpen(false);
    }
    if (showIncomplete) {
      // Find incomplete rows
      const updatedIncompleteIndices = fields.reduce((acc, field, index) => {
        const fieldError = errors.metadata?.authors?.[index];
        const authorData = authors?.[index];
        const hasErrors = Boolean(
          fieldError?.firstName ||
            fieldError?.lastName ||
            fieldError?.email ||
            fieldError?.affiliation ||
            !authorData?.orcid
        );
        if (hasErrors) {
          acc.push(index);
        }
        return acc;
      }, [] as number[]);
      setIncompleteRows(updatedIncompleteIndices);
    }
  };

  const AuthorTableRow = ({
    author,
    index,
    sortable,
    initialAuthor
  }: {
    author: IAuthorWithContribution & { id: string };
    index: number;
    sortable: any;
    initialAuthor: IAuthorWithEmail[];
  }) => {
    const isDuplicateEmail =
      author.email && duplicateEmails.has(author.email.toLowerCase().trim());

    const canDelete = ![
      authorRoleMap['Submitting'],
      authorRoleMap['Corresponding']
    ].some(email => email === author.email);

    const hasName =
      [author.firstName, author.middleName, author.lastName].filter(Boolean)
        .length > 0;

    return (
      <>
        <Td {...sortable.attributes} {...sortable.listeners} cursor="grab">
          <FontAwesomeIcon icon={faGripDotsVertical} />
        </Td>
        {!isRevision && (
          <Td>
            <Checkbox
              ariaLabel={author.firstName}
              id={author.firstName}
              name={author.firstName}
              isChecked={selectedAuthors.includes(author.id)}
              isDisabled={!isSelectable(author)}
              onChange={() => handleRowCheckboxChange(author.id)}
            >
              {''}
            </Checkbox>
          </Td>
        )}
        <Td>{index + 1}</Td>

        <Td ps={1} cursor="pointer" _hover={{ bg: 'gray.50' }}>
          {hasName ? (
            <Text textStyle="actionLabel" data-cy="author-name">
              {[author.firstName, author.middleName, author.lastName]
                .filter(Boolean)
                .join(' ')}
            </Text>
          ) : (
            <Text
              color="actionSecondaryCautionContent"
              textStyle="actionLabel"
              data-cy="no-name-data"
            >
              No Name Data
            </Text>
          )}

          {author?.email ? (
            <Text
              textStyle="textContext"
              wordBreak="break-word"
              whiteSpace="normal"
              color={
                isDuplicateEmail ? 'actionSecondaryCautionContent' : undefined
              }
            >
              {author?.email}
            </Text>
          ) : (
            <Text color="actionSecondaryCautionContent" textStyle="textContext">
              No Email address provided
            </Text>
          )}
          {orcidField !== 'hidden' && (
            <>
              {author.orcid ? (
                <Flex alignItems="center" flexDirection="row" gap={1}>
                  <OrcidIDIcon value={author.orcid} />
                  <Text textStyle="textContext">{author.orcid}</Text>
                </Flex>
              ) : (
                <Text
                  color={
                    orcidField === 'mandatory'
                      ? 'actionSecondaryCautionContent'
                      : ''
                  }
                  textStyle="textContext"
                >
                  No ORCID provided
                </Text>
              )}
            </>
          )}
          <Flex gap={4}>
            {Object.keys(authorRoleMap).map(role => {
              if (
                authorRoleMap[role as keyof IAuthorRolesMap] === author.email
              ) {
                return (
                  <Badge variant="attention" size="context">
                    {role} Author
                  </Badge>
                );
              }
            })}
          </Flex>
        </Td>
        {affiliationField !== 'hidden' && (
          <Td ps={0}>
            <Flex align="flex-start" gap={2}>
              {author?.ringgoldId && <RinggoldBadge />}
              {author?.affiliation ? (
                <Flex flexDirection="column">
                  <Text textStyle="textContext" data-cy="author-affiliation">
                    {author.affiliation}
                    {author.city && `, ${author.city}`}
                    {author.country && `, ${author.country}`}
                    {author.postalCode && `, ${author.postalCode}`}
                  </Text>
                  {!author.city && (
                    <Text
                      color={
                        affiliationField === 'mandatory'
                          ? 'actionSecondaryCautionContent'
                          : ''
                      }
                      textStyle="textContext"
                      data-cy="no-affiliation-city"
                    >
                      No City provided
                    </Text>
                  )}
                  {!author.country && (
                    <Text
                      color={
                        affiliationField === 'mandatory'
                          ? 'actionSecondaryCautionContent'
                          : ''
                      }
                      textStyle="textContext"
                      data-cy="no-affiliation-country"
                    >
                      No Country provided
                    </Text>
                  )}
                </Flex>
              ) : (
                <Text
                  color={
                    affiliationField === 'mandatory'
                      ? 'actionSecondaryCautionContent'
                      : ''
                  }
                  textStyle="textContext"
                  data-cy="no-affiliation-data"
                >
                  No Affiliation provided
                </Text>
              )}
            </Flex>
          </Td>
        )}

        {contributionsField !== 'hidden' && (
          <Td ps={0} data-cy="contributions-cell">
            <ContributionsBadge contributions={author.contributions} />
          </Td>
        )}

        <Td>
          <ButtonIcon
            ariaLabel={''}
            icon={<FontAwesomeIcon icon={faPencil} />}
            onClick={() => handleAuthorEditModal(index)}
          />
        </Td>
        <Td>
          {canDelete && (
            <ButtonIcon
              ariaLabel={''}
              icon={<FontAwesomeIcon icon={faTrashAlt} />}
              onClick={() => {
                if (fields.length === 1) {
                  setValue('metadata.authors', [], {
                    shouldValidate: true
                  });
                  setSelectedAuthors([]);
                } else {
                  remove(index);
                  setSelectedAuthors([]);
                }
              }}
              disabled={
                isRevision &&
                initialAuthor.some(user => user.email === author.email)
              }
              data-cy={`delete-author-button-${index}`}
            />
          )}
        </Td>
      </>
    );
  };

  const { keycloak } = useKeycloak();
  const impersonator = keycloak?.tokenParsed?.IMPERSONATOR_USERNAME;

  const showDiscardChangesModal = watch('showDiscardChangesModal');

  const parentSensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } })
  );

  const onParentDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    const oldIndex = fields.findIndex(f => f.id === active.id);
    const newIndex = fields.findIndex(f => f.id === over.id);
    if (oldIndex === -1 || newIndex === -1) return;

    if (oldIndex !== newIndex) {
      move(oldIndex, newIndex);
    }
  };

  return (
    <Layout width="100%" paddingY={2} data-cy="submission-flow-layout">
      {/* confirmation modal */}
      {showDiscardChangesModal && (
        <Modal
          isOpen
          onClose={() => {}}
          closeButtonProps={{ hidden: true }}
          width={{ base: '90vw', sm: '448px' } as any}
          data-cy="confirmation-modal"
        >
          <Flex padding={8} gap={6} flexDirection="column">
            <Box color="actionSecondaryCautionContent" textAlign="center">
              <FontAwesomeIcon fontSize={32} icon={faExclamationTriangle} />
            </Box>

            <Flex flexDirection="column" gap={2}>
              <Text textStyle="headingGroup" textAlign="center">
                Close Without Saving?
              </Text>
              <Text textAlign="center">
                You have unsaved changes. Do you want to discard them or save
                before exiting?
              </Text>
            </Flex>

            <Flex gap={2} flexDirection="column" marginTop={6}>
              <Button
                onClick={() => {
                  resetAuthorDataToInitialState();
                  onClose();
                }}
                variant="secondary"
                data-cy="confirmation-modal-cancel-button"
              >
                Discard
              </Button>
              <Button
                type="submit"
                variant="caution"
                onClick={() => setValue('showDiscardChangesModal', false)}
                data-cy="confirmation-modal-close-button"
              >
                Save Changes
              </Button>
            </Flex>
          </Flex>
        </Modal>
      )}

      <Flex paddingX={2}>
        <ButtonText
          variant="primary"
          onClick={() => {
            resetAuthorDataToInitialState();
            onClose();
          }}
        >
          <FontAwesomeIcon icon={faArrowLeft} /> Back
        </ButtonText>
      </Flex>

      <Flex
        flexDirection={'column'}
        height={{
          base: `calc(100svh - ${impersonator ? '200px' : '140px'})`,
          md: impersonator ? '70vh' : '85vh'
        }}
        paddingTop={2}
        paddingX={{ base: 2, md: 4 }}
      >
        <Flex
          flexDirection={{ base: 'column', sm: 'row' }}
          justifyContent="space-between"
          gap={{ base: 1, sm: 0 }}
        >
          <Flex alignItems="center" flexWrap="wrap" gap={1}>
            <Text textStyle="headingSection">
              {' '}
              {reviewAuthorInformation?.editAuthorInformation.mainLabel ??
                'Edit authors information'}
            </Text>
            <Tooltip
              hasArrow
              shouldWrapChildren
              placement="top"
              label={
                <Text textStyle="textContext">
                  {
                    reviewAuthorInformation?.editAuthorInformation
                      .secondaryLabel
                  }
                </Text>
              }
              borderRadius={4}
              data-cy="info-tooltip"
            >
              <ButtonText>
                <FontAwesomeIcon icon={faInfoCircle} />
              </ButtonText>
            </Tooltip>
          </Flex>
        </Flex>

        <Flex
          marginTop={4}
          gap={4}
          flexDirection="column"
          flex={1}
          overflow="hidden"
        >
          <Flex
            flexDirection={{ base: 'column', md: 'row' }}
            alignItems={{ base: 'stretch', md: 'center' }}
            justifyContent="space-between"
            position="sticky"
            top={0}
            zIndex={1}
            backgroundColor={'surfaceSecondaryFill'}
            gap={{ base: 2, md: 0 }}
            paddingBottom={{ base: 2, md: 0 }}
          >
            <Flex
              flexDirection="row"
              alignItems="center"
              justifyContent="space-between"
              gap={2}
            >
              <Button
                onClick={() => {
                  insert(authors.length, DEFAULT_VALUE, {
                    shouldFocus: true
                  });
                  // trigger the validation immediately so we can show error for newly added row
                  trigger('metadata.authors');
                  if (showIncomplete) {
                    setIncompleteRows(prev => [
                      0,
                      ...prev.map(index => index + 1)
                    ]);
                  }
                  setIsAuthorEditModalOpen(true);
                  setIsAddNewAuthorModalOpen(true);
                  setSelectedAuthorIndex(authors.length); // new author index
                  setSelectedAuthors([]);
                }}
                variant="secondary"
                data-cy="add-author-button"
                leftIcon={<FontAwesomeIcon icon={faUserPlus} />}
              >
                Add New Author
              </Button>
              {!isRevision && (
                <ButtonText
                  variant="caution"
                  onClick={() => setDeleteAuthors(true)}
                  disabled={selectedAuthors.length === 0}
                  leftIcon={<FontAwesomeIcon icon={faTrashAlt} />}
                >
                  Delete Author
                  {selectedAuthors.length > 1
                    ? `s (${selectedAuthors.length}) `
                    : ' '}
                </ButtonText>
              )}
            </Flex>

            <Flex
              gap={2}
              alignItems="center"
              justifyContent={{ base: 'flex-start', md: 'flex-end' }}
              flexShrink={0}
            >
              <Text
                textStyle="textBody"
                color="actionSecondaryContent"
                whiteSpace="nowrap"
              >
                Filter by
              </Text>
              <FilterChip
                ariaLabel="incomplete-author-filter"
                value={showIncomplete}
                onChange={handleShowIncompleteToggle}
                iconLeft={<FontAwesomeIcon icon={faBarsFilter} />}
                data-cy="incomplete-entries-filter"
              >
                Incomplete Entries
              </FilterChip>
            </Flex>
          </Flex>

          <Box
            flex={1}
            minH={0}
            overflowY="auto"
            overflowX="auto"
            sx={{ WebkitOverflowScrolling: 'touch' }}
          >
            {isRevision && (
              <Banner
                variant="attention"
                heading="Important: Managing authorship changes"
                content="Authors cannot be removed during the revision stage. To request removal of an author, please contact the publisher/editorial office for approval. If you are adding a new author, a valid justification is required and must be clearly explained in the revised cover letter."
              />
            )}
            {hasDuplicateEmails && (
              <Box marginY={4} width="100%" data-cy="validation-errors-banner">
                <Banner
                  variant="error"
                  heading="Duplicate emails found"
                  content="Each author must have a unique email."
                />
              </Box>
            )}
            {hasValidationErrors &&
              !hasDuplicateEmails &&
              fields.length > 0 && (
                <Box
                  marginY={4}
                  width="100%"
                  data-cy="validation-errors-banner"
                >
                  <Banner
                    variant="error"
                    heading="Missing Author Info"
                    content="Please enter all missing Names, Emails, ORCID and Affiliations."
                  />
                </Box>
              )}
            {noResultsFound ? (
              renderEmptyState()
            ) : (
              <>
                <Text textStyle="headingSection" mt={4}>
                  Author{fields.length > 1 && 's'} (
                  {showIncomplete ? incompleteRows.length : fields.length})
                </Text>
                <DndContext
                  sensors={parentSensors}
                  collisionDetection={closestCenter}
                  onDragEnd={onParentDragEnd}
                  modifiers={[restrictToVerticalAxis, restrictToParentElement]}
                >
                  <Table
                    data-cy="author-table"
                    minWidth="1010px"
                    width="100%"
                    backgroundColor="white"
                  >
                    <Tr
                      data-cy="author-table-header"
                      data-testId="author-table-header"
                      gap={4}
                      sx={{
                        '& th': {
                          textTransform: 'none',
                          color: 'black',
                          background: 'white'
                        }
                      }}
                    >
                      <Th width="1%"></Th>
                      {!isRevision && (
                        <Th>
                          <Checkbox
                            ariaLabel="All"
                            id="all"
                            name="all"
                            isChecked={allSelected}
                            onChange={e => handleSelectAll(e.target.checked)}
                          >
                            {''}
                          </Checkbox>
                        </Th>
                      )}
                      <Th>
                        <Text textStyle="headingItem" whiteSpace="nowrap">
                          S.No.
                        </Text>
                      </Th>
                      <Th>
                        <Text textStyle="headingItem" whiteSpace="nowrap">
                          Author
                        </Text>
                      </Th>

                      {affiliationField !== 'hidden' && (
                        <Th data-cy="affiliation-header-cell">
                          <Text textStyle="headingItem" whiteSpace="nowrap">
                            Affiliation
                          </Text>
                        </Th>
                      )}

                      {contributionsField !== 'hidden' && (
                        <Th ps={0}>
                          <Flex flex={1} alignItems="center" display={'flex'}>
                            <Text textStyle="headingItem" whiteSpace={'nowrap'}>
                              CRediT Contributions
                              <Tooltip
                                hasArrow
                                shouldWrapChildren
                                placement="top"
                                label="Contributor Role Taxonomy (CRediT) Contributions"
                                borderRadius={4}
                                data-cy="contributions-info-tooltip"
                              >
                                <ButtonIcon
                                  size="small"
                                  ariaLabel="Back to workspace settings"
                                  icon={<FontAwesomeIcon icon={faInfoCircle} />}
                                  data-cy="contributions-info-icon"
                                />
                              </Tooltip>
                            </Text>
                          </Flex>
                        </Th>
                      )}

                      <Th />
                      <Th />
                    </Tr>
                    <SortableContext
                      items={fields.map(f => f.id)}
                      strategy={verticalListSortingStrategy}
                    >
                      <Tbody marginBottom={8}>
                        {fields.map(({ id }, index) => (
                          <React.Fragment key={index}>
                            {showIncomplete &&
                            !incompleteRows.includes(index) ? null : (
                              <SortableRow id={id} key={id}>
                                {(sortable: any) => (
                                  <AuthorTableRow
                                    key={id}
                                    author={{
                                      ...authors[index]!,
                                      id
                                    }}
                                    index={index}
                                    sortable={sortable}
                                    initialAuthor={initialAuthor}
                                  />
                                )}
                              </SortableRow>
                            )}
                          </React.Fragment>
                        ))}
                      </Tbody>
                    </SortableContext>
                  </Table>
                </DndContext>
              </>
            )}
          </Box>
        </Flex>
      </Flex>

      {isAuthorEditModalOpen && selectedAuthorIndex !== null && (
        <EditAuthorInformation
          isOpen={isAuthorEditModalOpen}
          onClose={() => {
            handleCloseEditModal();
            if (isAddNewAuthorModalOpen) {
              remove(selectedAuthorIndex);
              setIsAddNewAuthorModalOpen(false);
            }
          }}
          index={selectedAuthorIndex}
          author={authors?.[selectedAuthorIndex]}
          onApply={onApply}
          customSetValues={setValue}
          authors={authors}
          config={config}
          authorRoleMap={authorRoleMap}
        />
      )}
      {deleteAuthors && (
        <DeleteAuthorsModal
          onClose={() => setDeleteAuthors(false)}
          isOpen={deleteAuthors}
          onDelete={handleDeleteSelected}
          selectedAuthors={selectedAuthors}
        />
      )}
    </Layout>
  );
};
